import React from "react";
import { BsImages } from "react-icons/bs";
import { Modal } from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/zoom";
import { Navigation, Pagination, Zoom } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { MediaCategory } from "../../../graphql/operations";
import useWindowSize from "../../../utils/useWindowSize";
import { CloudinaryImage, CloudinaryVideo } from "../../Atoms/Cloudinary";
import styles from "./ImageGallery.module.scss";

type GalleryPhoto = {
  url: string;
  type: string;
  category?: MediaCategory;
  id?: string;
};
type GalleryProps = {
  isProductPage?: boolean;
  showViewAllButton?: boolean;
  buttonClassName?: string;
  setGalleryOpen: Function;
  openGallery: boolean;
  photos: GalleryPhoto[];
  setInitialImage?: Function;
  initialImage?: string;
  videos?: {
    url: string;
    category?: MediaCategory;
    type: string;
  }[];
  onClickPhoto?: (photoIndex: number, photo: GalleryPhoto) => void;
};

const PlanImageGallery = ({
  photos,
  buttonClassName,
  openGallery,
  setGalleryOpen,
  isProductPage,
  showViewAllButton = true,
  initialImage,
  videos,
  onClickPhoto,
  setInitialImage,
}: GalleryProps) => {
  const { isMobile } = useWindowSize();
  const [categoryFilter, setCategoryFilter] = React.useState(null);
  const [open, setOpen] = React.useState(false);

  const [openSecond, setOpenSecond] = React.useState<{
    state: boolean;
    index: undefined | number;
  }>({
    state: false,
    index: undefined,
  });

  React.useEffect(() => {
    setOpen(openGallery);

    const zE = (window as any).zE;
    if (!zE) return;

    if (openGallery || openSecond.state) {
      zE("messenger", "hide");
    } else {
      zE("messenger", "show");
    }
  }, [openGallery, openSecond.state]);

  const categories = [];

  for (let i = 0; i < photos.length; i++) {
    if (
      photos[i].category !== null &&
      categories.filter((c) => c?.id === photos[i].category?.id).length === 0
    ) {
      categories.push(photos[i].category);
    }
  }

  const filteredPhotos =
    categoryFilter !== null
      ? photos.filter((p) => p.category?.id === categoryFilter)
      : photos;

  React.useEffect(() => {
    if (initialImage) {
      const idx = filteredPhotos.findIndex((p) => p.url === initialImage);
      setOpenSecond((prev) => ({ ...prev, state: true, index: idx }));
    }
  }, [initialImage, filteredPhotos]);

  return (
    <>
      {showViewAllButton && (
        <div
          onClick={() => setGalleryOpen(true)}
          className={`${styles["BorderButton"]} ${buttonClassName ?? ""}`}
        >
          <BsImages
            size={20}
            style={{
              marginRight: 7,
            }}
          />
          <span
            style={{
              fontSize: 12,
              fontFamily: "sans-serif",
              paddingTop: "2px",
              fontWeight: "bold",
            }}
          >
            View All
          </span>
        </div>
      )}
      <Modal
        open={open}
        onClose={() => setGalleryOpen(false)}
        classNames={{
          overlay: styles["customGalleryOverlay"],
          modal: styles["customGalleryModal"],
        }}
        showCloseIcon={!isProductPage}
        blockScroll={false}
      >
        {isProductPage && (
          <div className={styles["GalleryNav"]}>
            <button
              onClick={() => setGalleryOpen(false)}
              className="react-responsive-modal-closeButton"
              data-testid="close-button"
              style={{
                marginTop: 0,
                marginRight: 10,
                float: "right",
                position: "static",
              }}
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 36 36"
                data-testid="close-icon"
              >
                <path d="M28.5 9.62L26.38 7.5 18 15.88 9.62 7.5 7.5 9.62 15.88 18 7.5 26.38l2.12 2.12L18 20.12l8.38 8.38 2.12-2.12L20.12 18z"></path>
              </svg>
            </button>

            <ul className={styles["PhotoSortTabs"]}>
              <li
                className={
                  categoryFilter === null && styles["isActiveCategory"]
                }
                onClick={() => setCategoryFilter(null)}
                onTouchEnd={() => setCategoryFilter(null)}
              >
                All <span>({photos.length})</span>
              </li>
              {videos && videos.length > 0 && (
                <li
                  className={
                    categoryFilter === "videos" && styles["isActiveCategory"]
                  }
                  onClick={() => setCategoryFilter("videos")}
                  onTouchEnd={() => setCategoryFilter("videos")}
                >
                  Videos
                  <span>({videos.length})</span>
                </li>
              )}
              {categories
                .sort(function (a, b) {
                  return a.name.localeCompare(b.name);
                })
                .map((c, index) => {
                  return (
                    <li
                      className={
                        categoryFilter === c.id && styles["isActiveCategory"]
                      }
                      key={index}
                      onClick={() => setCategoryFilter(c.id)}
                      onTouchEnd={() => setCategoryFilter(c.id)}
                    >
                      {c.name}{" "}
                      <span>
                        ({photos.filter((p) => p.category?.id === c.id).length})
                      </span>
                    </li>
                  );
                })}
            </ul>
          </div>
        )}
        <div className={!isProductPage && styles["Gallery"]}>
          <div className={styles["GalleryImageContainer"]}>
            {filteredPhotos.map((photo, index) => {
              return (
                <div key={index} className={styles["GalleryImageWrapper"]}>
                  <div
                    className={styles["GalleryImage"]}
                    onClick={() => {
                      setOpenSecond({
                        state: true,
                        index,
                      });
                      onClickPhoto?.(index, photo);
                    }}
                  >
                    <CloudinaryImage
                      src={photo.url}
                      transformations={{
                        mobile: ["h_250"],
                        tablet: ["h_400"],
                        web: ["h_280"],
                      }}
                    />
                  </div>
                  <div className={styles["ImageCategoryName"]}>
                    {photo.category?.name}
                  </div>
                </div>
              );
            })}
          </div>
          {categoryFilter === "videos" && (
            <div className={styles.VideosSection}>
              {videos.map((m, index) => {
                return (
                  <div className={styles.VideoHolder} key={index}>
                    <CloudinaryVideo
                      src={m}
                      muted
                      playsInline
                      controls
                      transformations={["h_200"]}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Modal>
      <Modal
        open={openSecond.state}
        onClose={() => {
          if (setInitialImage) {
            setInitialImage(undefined);
          }
          setOpenSecond({
            state: false,
            index: undefined,
          });
        }}
        classNames={{
          overlay: styles["secondGalleryOverlay"],
          modal: styles["secondModal"],
        }}
        center
      >
        <Swiper
          pagination={{
            type: "fraction",
          }}
          navigation={!isMobile}
          modules={[Pagination, Navigation, Zoom]}
          className={styles["mySwiper"]}
          initialSlide={openSecond.index}
          spaceBetween={!isMobile ? 500 : 0}
          zoom={true}
        >
          {filteredPhotos.map((image: { url: string }, i: React.Key) => {
            return (
              <SwiperSlide className={styles["ImageModal"]} key={i}>
                <div className="swiper-zoom-container">
                  <CloudinaryImage
                    src={image.url}
                    transformations={["h_500", "c_fill"]}
                    objFit={"contain"}
                  />
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </Modal>
    </>
  );
};

export default PlanImageGallery;
